import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
export function QuickQuoteForm() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    eventType: '',
    date: '',
    guests: '',
    name: '',
    email: '',
    phone: '',
    details: ''
  });
  const updateForm = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const nextStep = () => setStep(prev => Math.min(prev + 1, 3));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, handle submission here
    alert('Quote request submitted successfully!');
    setStep(1);
    setFormData({
      eventType: '',
      date: '',
      guests: '',
      name: '',
      email: '',
      phone: '',
      details: ''
    });
  };
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  // Direction state for animation
  const [direction, setDirection] = useState(1);
  const handleNext = () => {
    setDirection(1);
    nextStep();
  };
  const handlePrev = () => {
    setDirection(-1);
    prevStep();
  };
  return <section className="py-24 px-6 md:px-12 bg-h2o-900 relative overflow-hidden" data-id="element-177">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-178">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-h2o-cyan/5 blur-[120px]" data-id="element-179" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-h2o-cyan-dark/10 blur-[120px]" data-id="element-180" />
      </div>

      <div className="max-w-2xl mx-auto relative z-10" data-id="element-181">
        <div className="text-center mb-12" data-id="element-182">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-183">
            Get Your Quick Quote
          </h2>
          <p className="text-lg text-slate-400" data-id="element-184">
            Tell us about your event and we'll prepare a personalized package.
          </p>
        </div>

        <div className="bg-h2o-800/50 backdrop-blur-md border border-slate-700/50 rounded-lg p-8 md:p-12 shadow-xl" data-id="element-185">
          {/* Step Indicator */}
          <div className="flex items-center justify-between mb-12 relative" data-id="element-186">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-slate-700 -z-10" data-id="element-187" />
            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-h2o-cyan -z-10 transition-all duration-500 ease-in-out" style={{
            width: `${(step - 1) / 2 * 100}%`
          }} data-id="element-188" />

            {[1, 2, 3].map(num => <div key={num} className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-colors duration-300 ${step >= num ? 'bg-h2o-cyan text-h2o-950 border-2 border-h2o-cyan' : 'bg-h2o-900 text-slate-400 border-2 border-slate-700'}`} data-id="element-189">
                {step > num ? <Check className="w-5 h-5" data-id="element-190" /> : num}
              </div>)}
          </div>

          <form onSubmit={handleSubmit} className="relative min-h-[320px]" data-id="element-191">
            <AnimatePresence mode="wait" custom={direction} data-id="element-192">
              {step === 1 && <motion.div key="step1" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="space-y-6 absolute w-full" data-id="element-193">
                  <div data-id="element-194">
                    <label htmlFor="eventType" className="block text-slate-300 text-sm font-medium mb-2" data-id="element-195">
                      Event Type
                    </label>
                    <select id="eventType" value={formData.eventType} onChange={e => updateForm('eventType', e.target.value)} className="w-full bg-h2o-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-h2o-cyan focus:ring-1 focus:ring-h2o-cyan outline-none transition-shadow appearance-none" required data-id="element-196">
                      <option value="" disabled data-id="element-197">
                        Select an event type
                      </option>
                      <option value="wedding" data-id="element-198">Wedding</option>
                      <option value="birthday" data-id="element-199">Birthday</option>
                      <option value="corporate" data-id="element-200">Corporate</option>
                      <option value="private" data-id="element-201">Private Party</option>
                      <option value="other" data-id="element-202">Other</option>
                    </select>
                  </div>
                  <div data-id="element-203">
                    <label htmlFor="date" className="block text-slate-300 text-sm font-medium mb-2" data-id="element-204">
                      Preferred Date
                    </label>
                    <input type="date" id="date" value={formData.date} onChange={e => updateForm('date', e.target.value)} className="w-full bg-h2o-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-h2o-cyan focus:ring-1 focus:ring-h2o-cyan outline-none transition-shadow [color-scheme:dark]" required data-id="element-205" />
                  </div>
                  <div className="pt-4 flex justify-end" data-id="element-206">
                    <button type="button" onClick={handleNext} disabled={!formData.eventType || !formData.date} className="bg-h2o-cyan text-h2o-950 font-semibold px-8 py-3 rounded-lg hover:bg-h2o-cyan-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible-ring" data-id="element-207">
                      Next Step
                    </button>
                  </div>
                </motion.div>}

              {step === 2 && <motion.div key="step2" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="space-y-6 absolute w-full" data-id="element-208">
                  <div data-id="element-209">
                    <label htmlFor="guests" className="block text-slate-300 text-sm font-medium mb-2" data-id="element-210">
                      Estimated Guests
                    </label>
                    <input type="number" id="guests" min="1" placeholder="e.g. 150" value={formData.guests} onChange={e => updateForm('guests', e.target.value)} className="w-full bg-h2o-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-h2o-cyan focus:ring-1 focus:ring-h2o-cyan outline-none transition-shadow" required data-id="element-211" />
                  </div>
                  <div data-id="element-212">
                    <label htmlFor="name" className="block text-slate-300 text-sm font-medium mb-2" data-id="element-213">
                      Your Name
                    </label>
                    <input type="text" id="name" placeholder="Jane Doe" value={formData.name} onChange={e => updateForm('name', e.target.value)} className="w-full bg-h2o-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-h2o-cyan focus:ring-1 focus:ring-h2o-cyan outline-none transition-shadow" required data-id="element-214" />
                  </div>
                  <div className="pt-4 flex justify-between" data-id="element-215">
                    <button type="button" onClick={handlePrev} className="text-slate-300 hover:text-white font-medium px-6 py-3 rounded-lg transition-colors focus-visible-ring" data-id="element-216">
                      Back
                    </button>
                    <button type="button" onClick={handleNext} disabled={!formData.guests || !formData.name} className="bg-h2o-cyan text-h2o-950 font-semibold px-8 py-3 rounded-lg hover:bg-h2o-cyan-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible-ring" data-id="element-217">
                      Next Step
                    </button>
                  </div>
                </motion.div>}

              {step === 3 && <motion.div key="step3" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="space-y-6 absolute w-full" data-id="element-218">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-219">
                    <div data-id="element-220">
                      <label htmlFor="email" className="block text-slate-300 text-sm font-medium mb-2" data-id="element-221">
                        Email Address
                      </label>
                      <input type="email" id="email" placeholder="jane@example.com" value={formData.email} onChange={e => updateForm('email', e.target.value)} className="w-full bg-h2o-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-h2o-cyan focus:ring-1 focus:ring-h2o-cyan outline-none transition-shadow" required data-id="element-222" />
                    </div>
                    <div data-id="element-223">
                      <label htmlFor="phone" className="block text-slate-300 text-sm font-medium mb-2" data-id="element-224">
                        Phone Number
                      </label>
                      <input type="tel" id="phone" placeholder="(555) 123-4567" value={formData.phone} onChange={e => updateForm('phone', e.target.value)} className="w-full bg-h2o-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-h2o-cyan focus:ring-1 focus:ring-h2o-cyan outline-none transition-shadow" required data-id="element-225" />
                    </div>
                  </div>
                  <div data-id="element-226">
                    <label htmlFor="details" className="block text-slate-300 text-sm font-medium mb-2" data-id="element-227">
                      Additional Details (Optional)
                    </label>
                    <textarea id="details" rows={3} placeholder="Tell us more about your vision..." value={formData.details} onChange={e => updateForm('details', e.target.value)} className="w-full bg-h2o-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-h2o-cyan focus:ring-1 focus:ring-h2o-cyan outline-none transition-shadow resize-none" data-id="element-228" />
                  </div>
                  <div className="pt-4 flex justify-between items-center gap-4" data-id="element-229">
                    <button type="button" onClick={handlePrev} className="text-slate-300 hover:text-white font-medium px-4 py-3 rounded-lg transition-colors focus-visible-ring whitespace-nowrap" data-id="element-230">
                      Back
                    </button>
                    <button type="submit" disabled={!formData.email || !formData.phone} className="w-full bg-h2o-cyan text-h2o-950 font-bold px-8 py-3 rounded-lg hover:bg-h2o-cyan-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible-ring" data-id="element-231">
                      Submit Request
                    </button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </form>
        </div>
      </div>
    </section>;
}